// /opt/dashboard/frontend/src/pages/CustomPois.js
// Patch 397 — Custom POI editor page (sötét téma, api.js minta)

import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../api';

// Leaflet default marker icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const CATEGORIES = [
  { value: 'tourism_museum', label: 'Múzeum' },
  { value: 'tourism_attraction', label: 'Látnivaló' },
  { value: 'tourism_gallery', label: 'Galéria' },
  { value: 'tourism_artwork', label: 'Műalkotás' },
  { value: 'tourism_viewpoint', label: 'Kilátópont' },
  { value: 'tourism_information', label: 'Információ' },
  { value: 'tourism_hotel', label: 'Szálloda' },
  { value: 'historic_monument', label: 'Emlékmű' },
  { value: 'historic_memorial', label: 'Memorial' },
  { value: 'historic_castle', label: 'Vár' },
  { value: 'historic_ruins', label: 'Romok' },
  { value: 'historic_archaeological_site', label: 'Régészeti hely' },
  { value: 'amenity_restaurant', label: 'Étterem' },
  { value: 'amenity_cafe', label: 'Kávézó' },
  { value: 'amenity_bar', label: 'Bár' },
  { value: 'amenity_pub', label: 'Kocsma' },
  { value: 'amenity_fast_food', label: 'Gyorsétterem' },
  { value: 'amenity_theatre', label: 'Színház' },
  { value: 'amenity_cinema', label: 'Mozi' },
  { value: 'amenity_library', label: 'Könyvtár' },
  { value: 'amenity_place_of_worship', label: 'Templom' },
  { value: 'amenity_townhall', label: 'Városháza' },
  { value: 'leisure_park', label: 'Park' },
  { value: 'leisure_garden', label: 'Kert' },
  { value: 'leisure_stadium', label: 'Stadion' },
  { value: 'leisure_sports_centre', label: 'Sportközpont' },
  { value: 'natural_peak', label: 'Hegycsúcs' },
  { value: 'natural_water', label: 'Víz' },
  { value: 'natural_beach', label: 'Strand' },
  { value: 'shop_other', label: 'Bolt' },
  { value: 'other', label: 'Egyéb' },
];

const LANGS = ['hu', 'en', 'de', 'fr', 'it', 'es', 'pl', 'zh', 'ja', 'pt', 'ko', 'sk', 'ro', 'sl'];

const emptyForm = {
  id: null,
  name: { hu: '', en: '' },
  lat: null,
  lon: null,
  category: 'tourism_attraction',
  customContext: '',
  tagsExtra: { website: '', phone: '' },
  active: true,
  sponsoredZoneId: '',
};

// Sötét téma stílusok
const C = {
  bg: '#1a1d27',
  panel: '#22252f',
  border: '#2a2e3a',
  text: '#e0e6ed',
  muted: '#8892a0',
  accent: '#00d4ff',
  warning: '#fbbf24',
  danger: '#ef4444',
  success: '#22c55e',
};

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 4,
  color: C.text,
  fontSize: 13,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const labelStyle = { display: 'block', fontSize: 11, color: C.muted, marginBottom: 4, marginTop: 8 };

const btnStyle = (variant = 'default') => ({
  padding: '6px 12px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  marginRight: 6,
  background: variant === 'primary' ? C.accent : variant === 'danger' ? C.danger : C.border,
  color: variant === 'primary' ? '#000' : C.text,
});

function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) { onClick(e.latlng); },
  });
  return null;
}

export default function CustomPois() {
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [osmDups, setOsmDups] = useState(null);
  const [filter, setFilter] = useState({ category: '', search: '', includeInactive: false });
  const center = [47.1747, 20.1859]; // Szolnok default

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const j = await api.customPois.list(filter.includeInactive);
      setPois(j.pois || []);
    } catch (e) {
      alert('Lista betöltési hiba: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filter.includeInactive]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleMapClick = ({ lat, lng }) => {
    setForm({ ...emptyForm, lat, lon: lng });
    setShowForm(true);
    setOsmDups(null);
  };

  const handleSubmit = async (skipDupCheck = false) => {
    if (!form.name.hu || !form.name.en) return alert('Magyar és angol név kötelező');
    if (form.lat == null || form.lon == null) return alert('Koordináta kötelező (kattints a térképre)');

    if (!form.id && !skipDupCheck && !osmDups) {
      try {
        const j = await api.customPois.checkOsmDup(form.lat, form.lon, 50);
        if (j.duplicates && j.duplicates.length > 0) {
          setOsmDups(j.duplicates);
          return;
        }
      } catch {
        // soft fail
      }
    }

    const tagsExtra = {};
    if (form.tagsExtra.website) tagsExtra.website = form.tagsExtra.website;
    if (form.tagsExtra.phone) tagsExtra.phone = form.tagsExtra.phone;

    const cleanName = {};
    for (const k of Object.keys(form.name)) {
      if (form.name[k] && form.name[k].trim()) cleanName[k] = form.name[k].trim();
    }

    const payload = {
      name: cleanName,
      lat: form.lat,
      lon: form.lon,
      category: form.category,
      customContext: form.customContext || null,
      tags: tagsExtra,
      active: form.active,
      sponsoredZoneId: form.sponsoredZoneId || null,
    };

    try {
      if (form.id) {
        await api.customPois.update(form.id, payload);
      } else {
        await api.customPois.create(payload);
      }
      setShowForm(false);
      setOsmDups(null);
      setForm(emptyForm);
      await fetchList();
    } catch (e) {
      alert('Mentési hiba: ' + e.message);
    }
  };

  const handleDelete = async (id, hard = false) => {
    const msg = hard ? 'Véglegesen törlöd? Nem visszaállítható.' : 'Inaktiválod? (soft delete)';
    if (!window.confirm(msg)) return;
    try {
      await api.customPois.delete(id, hard);
      await fetchList();
    } catch (e) {
      alert('Törlési hiba: ' + e.message);
    }
  };

  const handleEdit = (p) => {
    setForm({
      id: p.id,
      name: { ...{ hu: '', en: '' }, ...(p.name || {}) },
      lat: p.lat,
      lon: p.lon,
      category: p.category,
      customContext: p.customContext || '',
      tagsExtra: { website: p.tags?.website || '', phone: p.tags?.phone || '' },
      active: p.active !== false,
      sponsoredZoneId: p.sponsoredZoneId || '',
    });
    setShowForm(true);
    setOsmDups(null);
  };

  const filtered = pois.filter(p => {
    if (filter.category && p.category !== filter.category) return false;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      const name = (p.name?.hu || '') + ' ' + (p.name?.en || '');
      if (!name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', background: C.bg, color: C.text }}>
      {/* Sidebar */}
      <div style={{ width: 380, background: C.panel, overflowY: 'auto', borderRight: `1px solid ${C.border}`, padding: 16 }}>
        <h2 style={{ marginTop: 0, color: C.accent }}>📍 Custom POIs ({filtered.length})</h2>
        <div style={{ marginBottom: 12, padding: 10, background: C.bg, borderRadius: 4, fontSize: 12, color: C.muted }}>
          💡 Kattints a térképre új POI-hoz. A markert húzhatod szerkesztés közben.
        </div>
        <div style={{ marginBottom: 12 }}>
          <input placeholder="Keresés..." value={filter.search} onChange={e => setFilter({ ...filter, search: e.target.value })} style={inputStyle} />
          <select value={filter.category} onChange={e => setFilter({ ...filter, category: e.target.value })} style={{ ...inputStyle, marginTop: 6 }}>
            <option value="">Minden kategória</option>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <label style={{ fontSize: 12, marginTop: 8, display: 'block', color: C.muted }}>
            <input type="checkbox" checked={filter.includeInactive} onChange={e => setFilter({ ...filter, includeInactive: e.target.checked })} /> Inaktív POI-k mutatása
          </label>
        </div>
        {loading && <div style={{ color: C.muted }}>Betöltés...</div>}
        {filtered.map(p => (
          <div key={p.id} style={{ padding: 10, marginBottom: 6, background: p.active === false ? '#3a1f1f' : C.bg, borderRadius: 4, fontSize: 12, border: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 'bold', color: C.text }}>{p.name?.hu || p.name?.en || '(név hiányzik)'}</div>
            <div style={{ color: C.muted, marginTop: 2 }}>{CATEGORIES.find(c => c.value === p.category)?.label || p.category}</div>
            <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>{p.lat?.toFixed(5)}, {p.lon?.toFixed(5)}</div>
            <div style={{ marginTop: 8 }}>
              <button onClick={() => handleEdit(p)} style={btnStyle()}>Edit</button>
              {p.active !== false && <button onClick={() => handleDelete(p.id, false)} style={btnStyle()}>Inaktív</button>}
              <button onClick={() => handleDelete(p.id, true)} style={btnStyle('danger')}>Hard del</button>
            </div>
          </div>
        ))}
      </div>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          <MapClickHandler onClick={handleMapClick} />
          {filtered.map(p => (
            <Marker
              key={p.id}
              position={[p.lat, p.lon]}
              draggable={form.id === p.id}
              eventHandlers={{
                click: () => handleEdit(p),
                dragend: (e) => {
                  if (form.id === p.id) {
                    const { lat, lng } = e.target.getLatLng();
                    setForm(prev => ({ ...prev, lat, lon: lng }));
                  }
                },
              }}
            />
          ))}
          {showForm && form.lat && form.lon && !pois.find(p => p.id === form.id) && (
            <Marker position={[form.lat, form.lon]} />
          )}
        </MapContainer>

        {/* Form panel */}
        {showForm && (
          <div style={{ position: 'absolute', top: 16, right: 16, background: C.panel, padding: 16, width: 400, maxHeight: 'calc(100% - 32px)', overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.5)', borderRadius: 6, zIndex: 1000, border: `1px solid ${C.border}`, color: C.text }}>
            <h3 style={{ marginTop: 0, color: C.accent }}>{form.id ? '✏️ POI szerkesztése' : '➕ Új POI'}</h3>

            {osmDups && osmDups.length > 0 && (
              <div style={{ background: '#3a2a1f', padding: 12, marginBottom: 12, borderRadius: 4, fontSize: 12, border: `1px solid ${C.warning}` }}>
                <b style={{ color: C.warning }}>⚠️ OSM-en már létezik:</b>
                <ul style={{ margin: '6px 0 8px 0', paddingLeft: 18 }}>
                  {osmDups.map((d, i) => <li key={i}>{d.name} ({d.distance}m)</li>)}
                </ul>
                <button onClick={() => handleSubmit(true)} style={btnStyle('primary')}>Mégis létrehozom</button>
                <button onClick={() => setOsmDups(null)} style={btnStyle()}>Mégse</button>
              </div>
            )}

            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              📌 {form.lat?.toFixed(6)}, {form.lon?.toFixed(6)}
            </div>

            <label style={labelStyle}>Kategória *</label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>

            <label style={labelStyle}>Magyar név *</label>
            <input value={form.name.hu} onChange={e => setForm({ ...form, name: { ...form.name, hu: e.target.value } })} style={inputStyle} />

            <label style={labelStyle}>Angol név *</label>
            <input value={form.name.en} onChange={e => setForm({ ...form, name: { ...form.name, en: e.target.value } })} style={inputStyle} />

            <details style={{ marginTop: 12, marginBottom: 8 }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: C.muted }}>További nyelvek (opcionális)</summary>
              {LANGS.filter(l => l !== 'hu' && l !== 'en').map(l => (
                <div key={l} style={{ marginTop: 6 }}>
                  <label style={{ fontSize: 10, color: C.muted }}>{l}:</label>
                  <input value={form.name[l] || ''} onChange={e => setForm({ ...form, name: { ...form.name, [l]: e.target.value } })} style={{ ...inputStyle, padding: '4px 8px' }} />
                </div>
              ))}
            </details>

            <label style={labelStyle}>Custom Context (LLM tényadatok, max 2000 ch)</label>
            <textarea value={form.customContext} onChange={e => setForm({ ...form, customContext: e.target.value })} rows={5} maxLength={2000} style={{ ...inputStyle, resize: 'vertical' }} />

            <label style={labelStyle}>Website</label>
            <input value={form.tagsExtra.website} onChange={e => setForm({ ...form, tagsExtra: { ...form.tagsExtra, website: e.target.value } })} style={inputStyle} />

            <label style={labelStyle}>Phone</label>
            <input value={form.tagsExtra.phone} onChange={e => setForm({ ...form, tagsExtra: { ...form.tagsExtra, phone: e.target.value } })} style={inputStyle} />

            <label style={labelStyle}>Sponsored Zone ID (opcionális)</label>
            <input value={form.sponsoredZoneId} onChange={e => setForm({ ...form, sponsoredZoneId: e.target.value })} style={inputStyle} />

            <label style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
              <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} /> Aktív
            </label>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
              <button onClick={() => handleSubmit()} style={btnStyle('primary')}>
                {form.id ? '💾 Mentés' : '➕ Létrehoz'}
              </button>
              <button onClick={() => { setShowForm(false); setForm(emptyForm); setOsmDups(null); }} style={btnStyle()}>Mégse</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
